import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  Ticket,
  Users,
  Minus,
  Plus,
  ShoppingCart,
  Calendar,
  Clock,
  MapPin,
  Check,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import StepAgentReferral from "../../components/media_and_events/conference_registration/StepAgentReferral";
import generalService from "../../api/GeneralService";

export default function EventTicketSelection() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [eventData, setEventData] = useState(location.state?.eventData || null);
  const [loading, setLoading] = useState(!location.state?.eventData);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showReferralStep, setShowReferralStep] = useState(true);
  const [referralData, setReferralData] = useState({
    hasReferralCode: null,
    agentReferralCode: "",
    validatedReferralCode: false,
    agentDetails: null,
  });
  const [referralErrors, setReferralErrors] = useState({});
  const [referralValidationState, setReferralValidationState] = useState({ isValidating: false, isValid: false });

  // Auto-save ticket selection changes
  useEffect(() => {
    if (selectedTicketType && eventData) {
      const bookingData = {
        eventData,
        ticketSelection: {
          ticketType: selectedTicketType,
          quantity,
          totalAmount: selectedTicketType.price * quantity,
        },
        guests: [],
      };
      
      // Preserve existing guest data if any
      const savedBookingData = sessionStorage.getItem(`eventBooking_${slug}`);
      if (savedBookingData) {
        try {
          const parsed = JSON.parse(savedBookingData);
          if (parsed.guests && parsed.guests.length > 0) {
            bookingData.guests = parsed.guests;
          }
        } catch (error) {
          console.error("Error loading existing guest data:", error);
        }
      }
      sessionStorage.setItem(`eventBooking_${slug}`, JSON.stringify(bookingData));
    }
  }, [selectedTicketType, quantity, eventData, slug]);

  // Fetch event details function
  const fetchEventDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await conferenceAndTradeMissionService.getEventDetails(slug);
      if (response.data?.status_code === 200 && response.data?.data?.event) {
        setEventData(response.data.data.event);
      } else {
        toast.error("Failed to load event details");
        navigate(`/event-details/${slug}`);
      }
    } catch (error) {
      toast.error("An error occurred while fetching event details");
      navigate(`/event-details/${slug}`);
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  // Load saved data from sessionStorage on mount
  useEffect(() => {
    const savedBookingData = sessionStorage.getItem(`eventBooking_${slug}`);
    if (savedBookingData) {
      try {
        const parsed = JSON.parse(savedBookingData);
        if (parsed.ticketSelection) {
          setSelectedTicketType(parsed.ticketSelection.ticketType);
          setQuantity(parsed.ticketSelection.quantity);
        }
        // Check if referral step was already completed
        if (parsed.referralCompleted) {
          setShowReferralStep(false);
          if (parsed.referralData) {
            setReferralData(parsed.referralData);
          }
        }
      } catch (error) {
        console.error("Error loading saved booking data:", error);
      }
    }
  }, [slug]);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.info("Please sign in to continue");
      navigate("/signin", { state: { from: `/event-details/${slug}/book` } });
      return;
    }

    if (!eventData) {
      fetchEventDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, eventData, navigate, slug]);

  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  const eventStartDate = eventData?.event_start_date || eventData?.event_date;
  const eventEndDate = eventData?.event_end_date || eventData?.event_date;
  const eventDateDisplay =
    eventStartDate && eventEndDate && eventStartDate !== eventEndDate
      ? `${formatDate(eventStartDate)} – ${formatDate(eventEndDate)}`
      : formatDate(eventStartDate || eventData?.event_date);

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  // API function to validate agent referral code
  const validateAgentReferral = async (code) => {
    try {
      const response = await generalService.validateAgentReferralCode(code);
      
      if (response.data?.status_code === 200) {
        return {
          success: true,
          message: response.data.reason || "Referral code verified successfully!",
          agentDetails: response.data.data?.agent,
        };
      } else {
        return {
          success: false,
          message: response.data?.reason || "Invalid referral code",
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.data?.reason || "An error occurred while validating agent code",
      };
    }
  };

  const handleReferralContinue = () => {
    // Validate referral step
    const newErrors = {};
    if (referralData.hasReferralCode === null) {
      newErrors.hasReferralCode = "Please indicate if you have a referral code";
      setReferralErrors(newErrors);
      toast.error("Please indicate if you have a referral code");
      return;
    }
    
    if (referralData.hasReferralCode === true) {
      if (!referralData.agentReferralCode?.trim()) {
        newErrors.agentReferralCode = "Please enter your referral code";
        setReferralErrors(newErrors);
        toast.error("Please enter your referral code");
        return;
      } else if (!referralData.validatedReferralCode) {
        newErrors.agentReferralCode = "Please verify your referral code before proceeding";
        setReferralErrors(newErrors);
        toast.error("Please verify your referral code before proceeding");
        return;
      }
    }

    // Save referral data to sessionStorage
    const savedBookingData = sessionStorage.getItem(`eventBooking_${slug}`);
    let bookingData = savedBookingData ? JSON.parse(savedBookingData) : {};
    
    bookingData.referralCompleted = true;
    bookingData.referralData = referralData;
    sessionStorage.setItem(`eventBooking_${slug}`, JSON.stringify(bookingData));

    // Proceed to ticket selection
    setShowReferralStep(false);
    setReferralErrors({});
  };

  const handleBackToReferral = () => {
    // Go back to referral step
    setShowReferralStep(true);
  };

  const handleContinue = () => {
    if (!selectedTicketType) {
      toast.error("Please select a ticket type");
      return;
    }

    if (quantity < 1) {
      toast.error("Please select at least one ticket");
      return;
    }

    // Save booking data to sessionStorage (preserve referral data)
    const savedBookingData = sessionStorage.getItem(`eventBooking_${slug}`);
    let bookingData = savedBookingData ? JSON.parse(savedBookingData) : {};
    
    bookingData.eventData = eventData;
    bookingData.ticketSelection = {
      ticketType: selectedTicketType,
      quantity,
      totalAmount: selectedTicketType.price * quantity,
    };
    if (!bookingData.guests) {
      bookingData.guests = []; // Will be updated in guest details page if needed
    }
    
    sessionStorage.setItem(`eventBooking_${slug}`, JSON.stringify(bookingData));

    // Navigate to guest details if quantity > 1, otherwise go to payment
    if (quantity > 1) {
      navigate(`/event-details/${slug}/guest-details`);
    } else {
      navigate(`/event-details/${slug}/event-payment`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-50">
        <div className="flex flex-col gap-4 items-center">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-emerald-200 animate-spin border-t-emerald-500" />
            <div className="flex absolute inset-0 justify-center items-center">
              <Ticket size={28} className="text-emerald-500" />
            </div>
          </div>
          <p className="text-lg font-semibold text-gray-600">Loading tickets...</p>
        </div>
      </div>
    );
  }

  if (!eventData) return null;

  const ticketTypes = eventData.ticket_types || [];
  const totalAmount = selectedTicketType ? selectedTicketType.price * quantity : 0;

  // Standard glass header for referral step
  const referralHeader = (
    <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r from-emerald-500/[0.08] via-teal-500/[0.06] to-transparent" aria-hidden />
      <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
            <Ticket size={20} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="mb-0.5 flex items-center justify-start">
              <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                Step 1
              </span>
            </div>
            <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
              Book Your Tickets
            </h1>
            <p className="mt-0.5 text-xs font-medium text-gray-600 sm:text-sm truncate">
              {eventData?.event_title}
            </p>
          </div>
        </div>
        <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
          <motion.button
            type="button"
            onClick={() => navigate(`/event-details/${slug}`)}
            whileHover={{ x: -2 }}
            whileTap={{ scale: 0.98 }}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-emerald-500/10 hover:text-emerald-700 sm:flex-none"
          >
            <ChevronLeft size={20} strokeWidth={2} />
            Back to Event
          </motion.button>
        </div>
      </div>
    </header>
  );

  // Show referral step first
  if (showReferralStep) {
    return (
      <div className="min-h-screen bg-slate-50">
        {referralHeader}

        <div className="px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <AnimatePresence mode="wait">
              <StepAgentReferral
                key="referral-step"
                formData={referralData}
                setFormData={setReferralData}
                errors={referralErrors}
                onValidateReferral={validateAgentReferral}
                onValidationStateChange={setReferralValidationState}
              />
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="sticky bottom-0 px-8 py-6 mt-8 bg-white rounded-xl border border-gray-200 shadow-lg"
            >
              <div className="flex justify-end items-center">
                <motion.button
                  onClick={handleReferralContinue}
                  disabled={
                    referralData.hasReferralCode === true && 
                    (!referralData.validatedReferralCode || referralValidationState.isValidating)
                  }
                  whileHover={
                    !(referralData.hasReferralCode === true && 
                    (!referralData.validatedReferralCode || referralValidationState.isValidating))
                      ? { scale: 1.02 }
                      : {}
                  }
                  whileTap={
                    !(referralData.hasReferralCode === true && 
                    (!referralData.validatedReferralCode || referralValidationState.isValidating))
                      ? { scale: 0.98 }
                      : {}
                  }
                  className="px-8 py-3 text-base font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg shadow-md transition-all hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue to Ticket Selection
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Standard glass header for ticket selection step
  const ticketHeader = (
    <header className="sticky top-16 z-30 mt-16 border-b border-white/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:top-20 sm:mt-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r from-emerald-500/[0.08] via-teal-500/[0.06] to-transparent" aria-hidden />
      <div className="relative mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
            <Ticket size={20} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="mb-0.5 flex items-center justify-start">
              <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                Step 2
              </span>
            </div>
            <h1 className="text-base font-bold tracking-tight text-gray-900 sm:text-xl">
              Select Your Tickets
            </h1>
            <p className="mt-0.5 text-xs font-medium text-gray-600 sm:text-sm truncate">
              {eventData?.event_title}
            </p>
          </div>
        </div>
        <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto">
          <motion.button
            type="button"
            onClick={handleBackToReferral}
            whileHover={{ x: -2 }}
            whileTap={{ scale: 0.98 }}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-emerald-500/10 hover:text-emerald-700 sm:flex-none"
          >
            <ChevronLeft size={20} strokeWidth={2} />
            <span className="sm:hidden">Referral</span>
            <span className="hidden sm:inline">Back to Referral</span>
          </motion.button>
          <motion.button
            type="button"
            onClick={() => navigate(`/event-details/${slug}`)}
            whileHover={{ x: -2 }}
            whileTap={{ scale: 0.98 }}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-emerald-500/10 hover:text-emerald-700 sm:flex-none"
          >
            <ChevronLeft size={20} strokeWidth={2} />
            <span className="sm:hidden">Event</span>
            <span className="hidden sm:inline">Back to Event</span>
          </motion.button>
        </div>
      </div>
    </header>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {ticketHeader}

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          {/* Event quick info below header */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap gap-3 mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
          >
            <div className="flex gap-2 items-center text-sm text-gray-700">
              <Calendar size={16} className="text-emerald-600 shrink-0" />
              <span>{eventDateDisplay}</span>
            </div>
            {((eventData.event_start_time || eventData.event_time) || (eventData.event_end_time || eventData.event_time)) && (
              <div className="flex gap-2 items-center text-sm text-gray-700">
                <Clock size={16} className="text-emerald-600 shrink-0" />
                <span>
                  {eventData.event_start_time && eventData.event_end_time && eventData.event_start_time !== eventData.event_end_time
                    ? `${formatTime(eventData.event_start_time)} – ${formatTime(eventData.event_end_time)}`
                    : formatTime(eventData.event_start_time || eventData.event_time)}
                </span>
              </div>
            )}
            <div className="flex gap-2 items-center text-sm text-gray-700">
              <MapPin size={16} className="text-emerald-600 shrink-0" />
              <span>{eventData.venue_name}</span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Left Column - Ticket Selection */}
            <div className="lg:col-span-2">
              {/* Step Indicator */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3 items-center p-4 mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200 shadow-md"
              >
                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-lg">
                  <span className="text-lg font-bold text-white">2</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-emerald-900">Step 2 of {quantity > 1 ? "4" : "3"}</p>
                  <p className="text-xs text-emerald-700">Choose your tickets</p>
                </div>
              </motion.div>

              {/* Ticket Types */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="mb-6 text-xl font-bold text-gray-900">Available Tickets</h2>
                <div className="space-y-4">
                  {ticketTypes.map((ticket, index) => {
                    const isSelected = selectedTicketType?.name === ticket.name;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.01 }}
                        onClick={() => setSelectedTicketType(ticket)}
                        className={`relative overflow-hidden p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-500 ring-2 ring-emerald-300 shadow-xl"
                            : "bg-white border-gray-200 hover:border-emerald-300 hover:shadow-lg"
                        }`}
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="flex absolute top-4 right-4 justify-center items-center w-8 h-8 bg-indigo-500 rounded-full shadow-lg"
                          >
                            <Check size={18} className="text-white" />
                          </motion.div>
                        )}

                        <div className="flex gap-4 items-start">
                          <div className={`flex-shrink-0 p-3 rounded-xl ${isSelected ? "bg-emerald-500" : "bg-gray-100"}`}>
                            <Ticket size={24} className={isSelected ? "text-white" : "text-gray-600"} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`text-lg font-bold mb-2 ${isSelected ? "text-emerald-900" : "text-gray-900"}`}>
                              {ticket.name}
                            </h3>
                            {ticket.description && (
                              <p className={`text-sm mb-4 ${isSelected ? "text-emerald-700" : "text-gray-600"}`}>
                                {ticket.description}
                              </p>
                            )}

                            {ticket.whats_included?.length > 0 && (
                              <div className="mb-4 space-y-2">
                                {ticket.whats_included.slice(0, 3).map((item, idx) => (
                                  <div key={idx} className="flex gap-2 items-start text-sm">
                                    <Check size={16} className="flex-shrink-0 mt-0.5 text-emerald-600" />
                                    <span className={isSelected ? "text-emerald-800" : "text-gray-700"}>{item}</span>
                                  </div>
                                ))}
                                {ticket.whats_included.length > 3 && (
                                  <p className="ml-6 text-xs font-medium text-emerald-600">
                                    +{ticket.whats_included.length - 3} more benefits
                                  </p>
                                )}
                              </div>
                            )}

                            <div className="flex gap-1 items-baseline">
                              {ticket.price === 0 || parseFloat(ticket.price) === 0 ? (
                                <span className={`text-2xl font-bold ${isSelected ? "text-emerald-600" : "text-gray-900"}`}>
                                  FREE
                                </span>
                              ) : (
                                <>
                                  <span className="text-sm font-medium text-gray-600">GH₵</span>
                                  <span className={`text-2xl font-bold ${isSelected ? "text-emerald-600" : "text-gray-900"}`}>
                                    {ticket.price}
                                  </span>
                                </>
                              )}
                              <span className="text-sm text-gray-500">per ticket</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Quantity Selector */}
              {selectedTicketType && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 mt-8 bg-white rounded-2xl border-2 border-gray-200 shadow-lg"
                >
                  <div className="flex gap-3 items-center mb-6">
                    <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md">
                      <Users size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Number of Tickets</h3>
                      <p className="text-sm text-gray-600">How many people will attend?</p>
                    </div>
                  </div>

                  <div className="flex gap-4 justify-center items-center">
                    <motion.button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      whileHover={quantity > 1 ? { scale: 1.1 } : {}}
                      whileTap={quantity > 1 ? { scale: 0.9 } : {}}
                      className={`flex justify-center items-center w-12 h-12 rounded-xl border-2 transition-all ${
                        quantity <= 1
                          ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-white border-emerald-300 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-500"
                      }`}
                    >
                      <Minus size={20} />
                    </motion.button>

                    <div className="flex flex-col items-center px-8 py-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-300">
                      <span className="text-4xl font-bold text-emerald-600">{quantity}</span>
                      <span className="text-sm font-medium text-emerald-700">
                        {quantity === 1 ? "Ticket" : "Tickets"}
                      </span>
                    </div>

                    <motion.button
                      onClick={() => setQuantity(quantity + 1)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl border-2 border-emerald-600 shadow-md transition-all hover:shadow-lg"
                    >
                      <Plus size={20} className="text-white" />
                    </motion.button>
                  </div>

                  {quantity > 1 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="flex gap-3 items-start p-4 mt-6 bg-blue-50 rounded-xl border border-blue-200"
                    >
                      <Info size={20} className="flex-shrink-0 text-blue-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="mb-1 font-semibold text-blue-900">Guest Information Required</p>
                        <p className="text-blue-700">
                          You'll need to provide details for {quantity - 1} guest{quantity - 1 > 1 ? "s" : ""} in the next step.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="sticky top-32 p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 shadow-xl"
              >
                <div className="flex gap-3 items-center pb-6 mb-6 border-b-2 border-gray-200">
                  <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-md">
                    <ShoppingCart size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Order Summary</h3>
                </div>

                {selectedTicketType ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-4"
                  >
                  <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                    <p className="mb-2 text-xs font-bold tracking-wide text-emerald-700 uppercase">
                      Ticket Type
                    </p>
                    <p className="text-lg font-bold text-emerald-900">{selectedTicketType.name}</p>
                  </div>

                    <div className="p-4 bg-white rounded-xl border border-gray-200">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-600">Price per ticket</span>
                        <span className="text-base font-bold text-gray-900">
                          {selectedTicketType.price === 0 || parseFloat(selectedTicketType.price) === 0 ? "FREE" : `GH₵ ${selectedTicketType.price}`}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">Quantity</span>
                        <span className="text-base font-bold text-gray-900">{quantity}</span>
                      </div>
                      <div className="flex justify-between items-center mt-3">
                        <span className="text-sm font-bold text-gray-700">Subtotal</span>
                        <span className="text-lg font-bold text-gray-900">
                          {totalAmount === 0 ? "FREE" : `GH₵ ${totalAmount}`}
                        </span>
                      </div>
                    </div>

                    <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold tracking-wide text-emerald-700 uppercase">Total Amount</span>
                        <span className="text-2xl font-bold text-emerald-600">
                          {totalAmount === 0 ? "FREE" : `GH₵ ${totalAmount}`}
                        </span>
                      </div>
                    </div>

                    <motion.button
                      onClick={handleContinue}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-4 w-full text-base font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-xl transition-all hover:from-emerald-600 hover:to-teal-700 hover:shadow-2xl"
                    >
                      Continue to {quantity > 1 ? "Guest Details" : "Payment"}
                    </motion.button>
                    
                    <motion.button
                      onClick={handleBackToReferral}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 mt-3 w-full text-base font-semibold text-gray-700 bg-white rounded-xl border-2 border-gray-300 shadow-md transition-all hover:bg-gray-50 hover:border-gray-400"
                    >
                      <div className="flex gap-2 justify-center items-center">
                        <ChevronLeft size={18} />
                        Back to Referral
                      </div>
                    </motion.button>
                  </motion.div>
                ) : (
                  <div className="p-8 text-center bg-gray-50 rounded-xl border-2 border-gray-300 border-dashed">
                    <Ticket size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-sm font-medium text-gray-600">
                      Select a ticket type to continue
                    </p>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

